import { uploadAssetToContentfulAPI } from './APIs';

/**
 * resolveUploadFields
 *
 * Before saving an experience, iterate over every `upload` field in the config JSON.
 * If a pending File exists in `pendingUploads` for that field, upload it to the
 * configured platform and replace the blob URL in fieldValues with the real CDN URL.
 *
 * @param {Object}  fieldValues              - current flat field values (may contain blob URLs)
 * @param {Map}     pendingUploads           - Map<fieldId, File>
 * @param {string}  configJsonString         - raw config_json string from the template
 * @param {string}  contentfulIntegrationId  - which Contentful integration to upload to
 * @param {string}  token                    - auth token
 * @param {string}  organizationId
 * @returns {Object} resolved fieldValues (new object, does not mutate original)
 */
export async function resolveUploadFields(
    fieldValues,
    pendingUploads,
    configJsonString,
    contentfulIntegrationId,
    token,
    organizationId
) {
    const resolved = { ...fieldValues };

    let configItems = [];
    try {
        configItems = JSON.parse(configJsonString);
    } catch (e) {
        console.error('resolveUploadFields: failed to parse config_json', e);
        return resolved;
    }

    for (const item of configItems) {
        if (item.input?.input_type !== 'upload') continue;

        // Use exp_json_field as the key — this is what fieldValues and pendingUploads are keyed by
        const fieldId = item.exp_json_field || item.id;
        if (!fieldId) continue;

        const file = pendingUploads instanceof Map ? pendingUploads.get(fieldId) : null;
        if (!file) continue;

        const platform = item.input?.platform;

        if (platform === 'contentful') {
            if (!contentfulIntegrationId) {
                throw new Error(`No Contentful integration selected for upload field "${item.input?.input_label || fieldId}". Please select a Contentful integration.`);
            }
            const result = await uploadAssetToContentfulAPI(token, organizationId, contentfulIntegrationId, file);
            if (result && result.sys) {
                if (fieldId.endsWith('.sys.id')) {
                    // Storing just the ID ensures that transformExperienceJSON outputs a correct sys object
                    // alongside the other flattened keys. We also ensure the link fields exist.
                    resolved[fieldId] = result.sys.id;
                    const baseField = fieldId.substring(0, fieldId.length - '.sys.id'.length);
                    resolved[`${baseField}.sys.type`] = 'Link';
                    resolved[`${baseField}.sys.linkType`] = 'Asset';
                } else {
                    resolved[fieldId] = { sys: result.sys };
                }
            } else if (result && result.url) {
                // Fallback if sys isn't returned for some reason
                resolved[fieldId] = result.url;
            }
        }
        // Future platforms can be added here as additional `else if` branches
    }

    return resolved;
}

/**
 * hasUploadFields
 *
 * Returns true if the given config JSON string has at least one upload-type field
 * with the given platform (or any platform if platform is not specified).
 *
 * @param {string} configJsonString
 * @param {string} [platform] - optional filter by platform
 * @returns {boolean}
 */
export function hasUploadFields(configJsonString, platform) {
    try {
        const items = JSON.parse(configJsonString);
        return items.some(
            (item) =>
                item.input?.input_type === 'upload' &&
                (!platform || item.input?.platform === platform)
        );
    } catch {
        return false;
    }
}
